import { useState } from 'react'
import type { TranscriptSpeaker } from '../types'

interface SpeakerListProps {
  speakers: TranscriptSpeaker[]
  speakerNames?: Record<string, string>
  onRename: (speakerId: string, newName: string) => void
  onAdd?: (name: string) => void
}

export default function SpeakerList({ speakers, speakerNames, onRename, onAdd }: SpeakerListProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [newSpeakerName, setNewSpeakerName] = useState('')

  const handleStartEdit = (speaker: TranscriptSpeaker) => {
    setEditingId(speaker.id)
    setEditValue(speakerNames?.[speaker.id] || speaker.name || speaker.id)
  }

  const handleSave = (speakerId: string) => {
    if (editValue.trim()) {
      onRename(speakerId, editValue.trim())
    }
    setEditingId(null)
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditValue('')
  }

  const handleAddSpeaker = () => {
    if (newSpeakerName.trim() && onAdd) {
      onAdd(newSpeakerName.trim())
      setNewSpeakerName('')
      setIsAdding(false)
    }
  }

  const handleCancelAdd = () => {
    setNewSpeakerName('')
    setIsAdding(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="font-semibold text-gray-800 mb-3">Спикеры</h3>
      <div className="flex flex-wrap gap-2">
        {speakers.map((speaker) => (
          <div
            key={speaker.id}
            className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
          >
            {editingId === speaker.id ? (
              <>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-32 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-primary-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave(speaker.id)
                    if (e.key === 'Escape') handleCancel()
                  }}
                />
                <button
                  onClick={() => handleSave(speaker.id)}
                  className="text-green-600 hover:text-green-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            ) : (
              <>
                <span className="text-sm font-medium text-gray-700">
                  {speakerNames?.[speaker.id] || speaker.name || speaker.id}
                </span>
                <span className="text-xs text-gray-400">
                  ({speaker.segments_count})
                </span>
                <button
                  onClick={() => handleStartEdit(speaker)}
                  className="text-gray-400 hover:text-gray-600 ml-1"
                  title="Переименовать"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </>
            )}
          </div>
        ))}

        {/* Add new speaker */}
        {onAdd && (
          isAdding ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
              <input
                type="text"
                value={newSpeakerName}
                onChange={(e) => setNewSpeakerName(e.target.value)}
                placeholder="Имя спикера"
                className="w-32 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-primary-500"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddSpeaker()
                  if (e.key === 'Escape') handleCancelAdd()
                }}
              />
              <button
                onClick={handleAddSpeaker}
                className="text-green-600 hover:text-green-800"
                title="Добавить"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button
                onClick={handleCancelAdd}
                className="text-gray-400 hover:text-gray-600"
                title="Отмена"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center gap-1 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700 transition"
              title="Добавить спикера"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm">Добавить</span>
            </button>
          )
        )}
      </div>
    </div>
  )
}
